<template>
  <div class="log-history-container">
    <div class="log-header">
      <h1>Log History - {{ logNo }}</h1>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading log history...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
    </div>

    <div v-else class="log-content">
      <div v-for="(categoryData, categoryName) in categories" :key="categoryName" class="category-section">
        <h2 class="category-title">{{ formatCategoryName(categoryName) }}</h2>

        <div class="table-responsive">
          <table class="table table-striped table-bordered">
            <thead>
              <tr>
                <th>Site Name</th>
                <th>Address</th>
                <th>NFD Date</th>
                <th>Status</th>
                <th>Problem</th>
                <th>Solution</th>
                <th>Sub Categories</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="kyt in categoryData.kyts" :key="kyt._id">
                <td>
                  <strong>{{ kyt.siteName }}</strong>
                  <div v-if="kyt.landmark" class="landmark">Landmark: {{ kyt.landmark }}</div>
                  <div v-if="kyt.isCompleted" class="completed-status">
                    <i class="fas fa-check-circle text-success"></i> Completed
                  </div>
                </td>
                <td>{{ kyt.siteAddress }}</td>
                <td>{{ formatDate(kyt.NFD) }}</td>
                <td>
                  <span :class="getStatusClass(kyt.status)">{{ kyt.status }}</span>
                </td>
                <td>
                  <div v-for="(formatData, index) in kyt.kytData[categoryName]" :key="index">
                    {{ formatData.problem }}
                  </div>
                </td>
                <td>
                  <div v-for="(formatData, index) in kyt.kytData[categoryName]" :key="index">
                    {{ formatData.solution }}
                  </div>
                </td>
                <td>
                  <div v-for="(formatData, index) in kyt.kytData[categoryName]" :key="index" class="format-section">
                    <div v-for="(subCat, subIndex) in formatData.subCategories" :key="subIndex" class="sub-category">
                      <strong>{{ subCat.subCategory }}</strong>
                      <div class="sub-cat-details">
                        <p><strong>Problem:</strong> {{ subCat.problem }}</p>
                        <p><strong>Solution:</strong> {{ subCat.solution }}</p>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'

const route = useRoute()
const logNo = ref(route.params.logNo as string)
const categories = ref<any>(null)
const loading = ref(true)
const error = ref('')
const baseUrl = process.env.VUE_APP_BASE_URL;

const fetchLogHistory = async () => {
  try {
    const response = await axios.get(`${baseUrl}api/operation/kyt/log/${logNo.value}`)
    categories.value = response.data.categories
  } catch (err) {
    error.value = 'Failed to fetch log history'
    console.error('Error fetching log history:', err)
  } finally {
    loading.value = false
  }
}

const formatCategoryName = (name: string) => {
  return name.charAt(0).toUpperCase() + name.slice(1)
}

const formatDate = (date: string) => {
  if (!date) return 'N/A'
  return new Date(date).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const getStatusClass = (status: string) => {
  return {
    'badge rounded-pill': true,
    'bg-success': status === 'approved',
    'bg-warning text-dark': status === 'pending',
    'bg-danger': status === 'rejected'
  }
}

onMounted(() => {
  fetchLogHistory()
})
</script>

<style scoped>
.log-history-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.log-header {
  margin-bottom: 30px;
}

.log-header h1 {
  font-size: 24px;
  color: #333;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.error-state {
  color: #dc3545;
  text-align: center;
  padding: 20px;
}

.category-section {
  margin-bottom: 40px;
}

.category-title {
  font-size: 20px;
  color: #2c3e50;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #eee;
}

.table-responsive {
  overflow-x: auto;
}

.table {
  width: 100%;
  margin-bottom: 1rem;
  color: #212529;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 0.75rem;
  vertical-align: top;
  border-top: 1px solid #dee2e6;
}

.table thead th {
  vertical-align: bottom;
  border-bottom: 2px solid #dee2e6;
  background-color: #f8f9fa;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05);
}

.table-bordered {
  border: 1px solid #dee2e6;
}

.table-bordered th,
.table-bordered td {
  border: 1px solid #dee2e6;
}

.landmark {
  font-size: 0.85em;
  color: #666;
}

.completed-status {
  color: #198754;
  margin-top: 5px;
}

.sub-category {
  margin-bottom: 10px;
  padding: 5px;
  border-bottom: 1px solid #eee;
}

.sub-cat-details p {
  margin: 5px 0;
  font-size: 0.9em;
}

.badge {
  padding: 0.5em 1em;
  font-size: 0.875em;
}

/* Responsive Design */
@media (max-width: 768px) {
  .table-responsive {
    max-width: 100%;
  }
}
</style>
